import type { TFunction } from '@mukil/i18n';
import { Alert } from '@jumpstone/ui-react';
import type { CompensationMutationFailure } from '../../data/use_employee_compensation';
import { conflictTitleKey } from './change_salary_utils';

/**
 * Renders a classified schedule/cancel failure. The backend's own message is
 * shown verbatim for `conflict`/`validation` -- it is a deliberately-authored,
 * caller-safe string (a draft pay run in the way, a date before the hire
 * date, ...), never an internal detail. `consumed` uses local copy so it is
 * translated; everything else falls back to the generic save error.
 *
 * Semantic colours here signal real conditions only: `warning` for a
 * conflict the operator can resolve, `danger` for a rejected/failed save.
 */
export function CompensationFailureAlert({
  failure,
  t,
}: {
  failure: CompensationMutationFailure;
  t: TFunction;
}): React.JSX.Element {
  switch (failure.kind) {
    case 'conflict':
      return (
        <Alert variant="warning" title={t(conflictTitleKey(failure))}>
          {failure.message}
        </Alert>
      );
    case 'consumed':
      return (
        <Alert variant="danger">
          {t('employees.compensation_cancel_consumed')}
        </Alert>
      );
    case 'validation':
      return (
        <Alert
          variant="danger"
          title={t('employees.form_validation_error_title')}
        >
          {failure.message}
        </Alert>
      );
    case 'entitlement_required':
      return (
        <Alert
          variant="info"
          title={t('employees.form_entitlement_required_title')}
        >
          {t('employees.form_entitlement_required_description')}
        </Alert>
      );
    case 'error':
      return (
        <Alert variant="danger" title={t('employees.form_save_error_title')}>
          {t('employees.form_save_error_description')}
        </Alert>
      );
  }
}
